import React from 'react';
import {
  Calendar,
  Briefcase,
  Users,
  Award,
  FileText,
  Clock,
  Zap,
  Plus,
  ArrowRight,
  ChevronRight,
  Shield,
  Sparkles,
  UserPlus
} from 'lucide-react';
import {
  PracticeAnalyticsSummary,
  PracticeCase,
  PracticeConsultation,
  PracticeClient,
  TimingRadarEvent,
  FollowUpItem
} from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { PracticeMetricCards } from './PracticeMetricCards';
import { CaseCard } from './CaseCard';

interface PracticeOverviewProps {
  analytics: PracticeAnalyticsSummary;
  cases: PracticeCase[];
  consultations: PracticeConsultation[];
  clients: PracticeClient[];
  timingEvents: TimingRadarEvent[];
  followUps: FollowUpItem[];
  lang: Language;
  onNavigateTab: (tabId: string) => void;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
  onOpenProAnalysis?: (caseItem: PracticeCase) => void;
  onOpenAcharyaConsultation?: (caseItem: PracticeCase) => void;
  onPrepareReport?: (caseItem: PracticeCase) => void;
  onOpenClientProfile: (client: PracticeClient) => void;
  onStartConsultation: (session: PracticeConsultation) => void;
  onNewClient: () => void;
  onNewCase: () => void;
  onNewConsultation: () => void;
}

export const PracticeOverview: React.FC<PracticeOverviewProps> = ({
  analytics,
  cases,
  consultations,
  clients,
  timingEvents,
  followUps,
  lang,
  onNavigateTab,
  onOpenCaseWorkspace,
  onOpenProAnalysis,
  onOpenAcharyaConsultation,
  onPrepareReport,
  onOpenClientProfile,
  onStartConsultation,
  onNewClient,
  onNewCase,
  onNewConsultation
}) => {
  const isHi = lang === 'hi';

  const todayStr = new Date().toISOString().split('T')[0];
  const todayConsultations = consultations.filter((c) => c.scheduledDateTime.startsWith(todayStr));
  const activeCases = cases.filter((c) => c.stage !== 'DELIVERED').slice(0, 4);
  const urgentTiming = timingEvents.filter((e) => e.daysUntilEvent <= 30).slice(0, 3);
  const dueFollowUps = followUps.filter((f) => f.status === 'PENDING' || f.status === 'OVERDUE').slice(0, 3);

  return (
    <div className="space-y-5">
      {/* 1. Top Executive KPI Bar */}
      <PracticeMetricCards
        analytics={analytics}
        todayConsultationsCount={todayConsultations.length}
        upcomingTimingCount={urgentTiming.length}
        lang={lang}
        onSelectMetric={(key) => {
          if (key === 'consultations_today') onNavigateTab('consultations');
          else if (key === 'active_cases' || key === 'awaiting_review') onNavigateTab('cases');
          else if (key === 'reports_pending') onNavigateTab('reports');
          else if (key === 'follow_ups_due') onNavigateTab('followups');
          else if (key === 'timing_events') onNavigateTab('timing');
        }}
      />

      {/* 2. Quick Action Bar */}
      <div className="bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent p-3.5 rounded-xl border border-astro-accent/30 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="text-xs font-bold text-astro-text dark:text-astro-muted">
            {isHi ? 'द्रुत कार्य केंद्र (Quick Actions):' : 'Practice Control Center:'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onNewClient}
            className="min-h-[34px] px-3 py-1.5 rounded-lg bg-white dark:bg-astro-secondary text-astro-text dark:text-astro-muted border border-astro-border dark:border-astro-strong-border hover:border-amber-400 font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <UserPlus className="w-3.5 h-3.5 text-amber-500" />
            <span>{isHi ? '+ नया क्लाइंट' : '+ New Client'}</span>
          </button>

          <button
            onClick={onNewCase}
            className="min-h-[34px] px-3 py-1.5 rounded-lg bg-white dark:bg-astro-secondary text-astro-text dark:text-astro-muted border border-astro-border dark:border-astro-strong-border hover:border-amber-400 font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <Briefcase className="w-3.5 h-3.5 text-blue-500" />
            <span>{isHi ? '+ नया केस' : '+ New Case'}</span>
          </button>

          <button
            onClick={onNewConsultation}
            className="min-h-[34px] px-3 py-1.5 rounded-lg bg-white dark:bg-astro-secondary text-astro-text dark:text-astro-muted border border-astro-border dark:border-astro-strong-border hover:border-amber-400 font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <Award className="w-3.5 h-3.5 text-orange-500" />
            <span>{isHi ? '+ सत्र नियत करें' : '+ Schedule Session'}</span>
          </button>

          <button
            onClick={() => onNavigateTab('reports')}
            className="min-h-[34px] px-3 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>{isHi ? 'रिपोर्ट केंद्र' : 'Reporting Centre'}</span>
          </button>
        </div>
      </div>

      {/* 3. Main Dashboard 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left 2 Cols: Today's Schedule & Active Cases Pipeline */}
        <div className="lg:col-span-2 space-y-4">
          {/* Today's Consultations Schedule */}
          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
                  {isHi ? 'आज का परामर्श सत्र कार्यक्रम' : "Today's Consultations & Live Sessions"}
                </h3>
              </div>
              <button
                onClick={() => onNavigateTab('consultations')}
                className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <span>View All</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {todayConsultations.length > 0 ? (
              <div className="space-y-2.5">
                {todayConsultations.map((sess) => (
                  <div
                    key={sess.id}
                    className="p-3 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border flex items-center justify-between gap-3 text-xs"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-astro-text dark:text-astro-muted">{sess.clientName}</span>
                        <span className="font-mono text-[10px] text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-1.5 py-0.2 rounded border border-[#E2DED5] dark:border-amber-800">
                          {sess.consultationNumber}
                        </span>
                        <span className="text-[10px] bg-astro-card dark:bg-astro-secondary px-1.5 py-0.2 rounded font-semibold text-astro-secondary-text dark:text-astro-muted">
                          {sess.meetingFormat}
                        </span>
                      </div>
                      <div className="text-astro-muted text-[11px] truncate pt-0.5 max-w-md">
                        {sess.question}
                      </div>
                    </div>

                    <button
                      onClick={() => onStartConsultation(sess)}
                      className="px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs flex items-center gap-1 shadow-xs cursor-pointer shrink-0"
                    >
                      <Award className="w-3.5 h-3.5" />
                      <span>{isHi ? 'परामर्श खोलें' : 'Start Session'}</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-xs text-astro-muted">
                {isHi ? 'आज कोई परामर्श सत्र नियत नहीं है।' : 'No consultations scheduled for today.'}
              </div>
            )}
          </div>

          {/* Active Cases Snapshot */}
          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-500" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
                  {isHi ? 'सक्रिय केस प्रवाह (Case Pipeline)' : 'Active Case Pipeline'}
                </h3>
              </div>
              <button
                onClick={() => onNavigateTab('cases')}
                className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <span>Full Pipeline</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activeCases.map((c) => (
                <CaseCard
                  key={c.id}
                  caseItem={c}
                  lang={lang}
                  onOpenCaseWorkspace={onOpenCaseWorkspace}
                  onOpenProAnalysis={onOpenProAnalysis}
                  onOpenAcharyaConsultation={onOpenAcharyaConsultation}
                  onPrepareReport={onPrepareReport}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Col: Timing Radar & Follow-Ups Snapshot */}
        <div className="space-y-4">
          {/* Urgent Timing Alerts */}
          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
                  {isHi ? 'आगामी गोचर अलर्ट (30 दिन)' : 'Timing Radar (30 Days)'}
                </h3>
              </div>
              <button
                onClick={() => onNavigateTab('timing')}
                className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <span>All Radar</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2">
              {urgentTiming.map((ev) => (
                <div
                  key={ev.id}
                  className="p-3 bg-astro-secondary-surface/50 dark:bg-astro-primary rounded-lg border border-[#E2DED5]/60 dark:border-astro-strong-border space-y-1 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-astro-text dark:text-astro-muted">{ev.clientName}</span>
                    <span className="text-[10px] font-bold text-[#52635F] dark:text-amber-400">
                      in {ev.daysUntilEvent} days
                    </span>
                  </div>
                  <p className="text-astro-secondary-text dark:text-astro-muted text-[11px] leading-tight">
                    {ev.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Follow-Ups */}
          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-500" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-astro-text dark:text-astro-muted">
                  {isHi ? 'लंबित फॉलो-अप' : 'Pending Follow-Ups'}
                </h3>
              </div>
              <button
                onClick={() => onNavigateTab('followups')}
                className="text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <span>View All</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2">
              {dueFollowUps.map((f) => (
                <div
                  key={f.id}
                  className="p-3 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-xs space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-astro-text dark:text-astro-muted">{f.clientName}</span>
                    <span className="text-[10px] font-semibold text-astro-muted">
                      Due: {f.dueDate}
                    </span>
                  </div>
                  <p className="text-astro-secondary-text dark:text-astro-muted text-[11px] leading-tight">
                    {f.reason}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
